import React from "react";
import SeoHead from "@/components/brand/SeoHead";
import GlowingGridBackground from "@/components/brand/GlowingGridBackground";
import GlassCard from "@/components/brand/GlassCard";
import FadeUp from "@/components/brand/FadeUp";

const LAST_UPDATED = "June 8, 2026";

/* Reusable section wrapper for long-form legal content */
const LegalSection = ({ heading, children, testId }) => (
    <FadeUp>
        <section
            className="mb-8"
            data-testid={testId}
            style={{ scrollMarginTop: "6rem" }}
        >
            {heading && (
                <h2
                    className="vfs-h3 mb-4"
                    style={{ fontSize: "1.5rem", fontWeight: 600 }}
                >
                    {heading}
                </h2>
            )}
            <div
                className="text-base sm:text-[1.0625rem] leading-relaxed space-y-4"
                style={{ color: "var(--text-primary)" }}
            >
                {children}
            </div>
        </section>
    </FadeUp>
);

export default function Privacy() {
    return (
        <>
            <SeoHead
                title="Privacy Policy | Vibe for Success"
                description="How Vibe for Success, Inc. collects, uses, and protects the information you share through interest forms, sponsor inquiries, and contact forms."
                path="/privacy"
            />

            {/* Hero */}
            <section
                className="vfs-section relative overflow-hidden"
                style={{ paddingTop: "clamp(2rem, 6vw, 4rem)" }}
                data-testid="privacy-hero"
            >
                <GlowingGridBackground accent="#8B5CF6" opacity={0.14} />
                <div className="vfs-container relative">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <span className="vfs-eyebrow">Privacy</span>
                            <h1
                                className="vfs-h1 mt-5"
                                data-testid="privacy-h1"
                            >
                                Privacy Policy
                            </h1>
                            <p
                                className="mt-4 text-sm"
                                style={{ color: "var(--text-secondary)" }}
                                data-testid="privacy-last-updated"
                            >
                                Last updated: {LAST_UPDATED}.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* Long-form content */}
            <section className="pb-24" data-testid="privacy-content">
                <div className="vfs-container">
                    <GlassCard
                        className="p-7 sm:p-10 lg:p-12 max-w-3xl mx-auto"
                        hoverLift={false}
                    >
                        <LegalSection testId="privacy-intro">
                            <p>
                                Vibe for Success, Inc. is a Florida nonprofit
                                corporation. This Privacy Policy explains what
                                information we collect through this website,
                                how we use it, and the steps we take to
                                protect it — especially when students and
                                parents submit information.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Information we collect"
                            testId="privacy-collect"
                        >
                            <p>
                                We collect information you choose to send us
                                through the website&apos;s forms, including:
                                name, email, phone, and organization or
                                business details (for sponsors and partners);
                                student first name, last initial, grade level,
                                city or area, interests, and a short answer
                                (for the Student Interest form);
                                parent/guardian name, email, and phone (for
                                the Parent Information and Student Interest
                                forms); and the contents of any free-text
                                message you write. We collect only what you
                                submit through the forms.
                            </p>
                            <p>
                                We do not store the IP address of people who
                                submit forms. Limited technical data (browser
                                user-agent, referring page, and the page path
                                of the form) may be saved with each submission
                                strictly to help us understand and improve the
                                website.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Student information"
                            testId="privacy-student"
                        >
                            <p>
                                The Student Interest form intentionally asks
                                for only a first name and a last initial — not
                                a full last name. Parent or guardian approval
                                is required before a student participates in
                                Future Builders Lab. We treat student
                                information with extra care and do not share
                                or sell it.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Parent and guardian information"
                            testId="privacy-parent"
                        >
                            <p>
                                Parent/guardian contact information is
                                collected so we can confirm approval, answer
                                questions, and coordinate participation. We do
                                not share or sell this information.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Sponsor and partner inquiries"
                            testId="privacy-sponsor-partner"
                        >
                            <p>
                                Information shared through the Sponsor Inquiry
                                and Partner Inquiry forms is used to respond
                                to the inquiry, follow up about pilot support,
                                and coordinate next steps. Sponsors and
                                partners may reach out to us directly to
                                update or remove their information.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="How we use information"
                            testId="privacy-use"
                        >
                            <ul className="space-y-2 pl-1">
                                {[
                                    "To respond to inquiries and follow up about Future Builders Lab.",
                                    "To coordinate parent/guardian approval and student participation.",
                                    "To send relevant updates about the pilot program (only when appropriate).",
                                    "To understand and improve how the website works.",
                                ].map((t, i) => (
                                    <li
                                        key={t}
                                        className="flex items-start gap-3"
                                    >
                                        <span
                                            className="mt-2.5 inline-block w-2 h-2 rounded-full flex-shrink-0"
                                            style={{
                                                background: [
                                                    "#EC4899",
                                                    "#8B5CF6",
                                                    "#3B82F6",
                                                    "#14B8A6",
                                                ][i % 4],
                                            }}
                                            aria-hidden="true"
                                        />
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </LegalSection>

                        <LegalSection
                            heading="How we protect information"
                            testId="privacy-protect"
                        >
                            <p>
                                Submissions are stored in a private database
                                that is not publicly accessible. Access is
                                limited to authorized members of Vibe for
                                Success, Inc. We do not sell, rent, or trade
                                personal information.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Youth privacy"
                            testId="privacy-youth"
                        >
                            <p>
                                Parent or guardian approval is required for
                                student participation. Student websites built
                                during Future Builders Lab are private or
                                unlisted by default unless a parent or
                                guardian chooses otherwise. Students are
                                guided not to publish sensitive personal
                                information, private contact details, exact
                                addresses, school schedules, or other unsafe
                                identifying information.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Cookies and analytics"
                            testId="privacy-cookies"
                        >
                            <p>
                                This website may use basic analytics in the
                                future to understand how visitors find and use
                                the site. If analytics are added, this Privacy
                                Policy will be updated to describe them
                                clearly. We do not currently use tracking
                                cookies for advertising.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Your choices"
                            testId="privacy-choices"
                        >
                            <p>
                                People can request that their information be
                                updated or deleted by emailing{" "}
                                <a
                                    href="mailto:hello@vibeforsuccess.org"
                                    className="underline"
                                    style={{
                                        color: "var(--accent-yellow)",
                                    }}
                                    data-testid="privacy-choices-email"
                                >
                                    hello@vibeforsuccess.org
                                </a>
                                . We will respond as soon as we reasonably
                                can.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Changes to this Privacy Policy"
                            testId="privacy-changes"
                        >
                            <p>
                                We may update this Privacy Policy as the
                                nonprofit and its programs grow. When we make
                                changes, we will update the &ldquo;Last
                                updated&rdquo; date at the top of this page.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Contact"
                            testId="privacy-contact"
                        >
                            <p>
                                For privacy questions, email{" "}
                                <a
                                    href="mailto:hello@vibeforsuccess.org"
                                    className="underline"
                                    style={{
                                        color: "var(--accent-yellow)",
                                    }}
                                    data-testid="privacy-contact-email"
                                >
                                    hello@vibeforsuccess.org
                                </a>
                                .
                            </p>
                        </LegalSection>
                    </GlassCard>
                </div>
            </section>
        </>
    );
}
